/**
 * klubb-player-package — soft-bundle PDP behavior.
 *
 * Pure helpers (exported, unit-tested) compute the personalization union, the
 * per-component scoped print properties, and the /cart/add.js payload. The
 * DOM wiring (variant pickers, personalization, multi-add) auto-inits in the
 * browser. Reuses validatePrintField/formatPrintSummary from klubb-product.js.
 */

// Union of every component's traede print capability — drives which shared
// inputs to render. `number` is the back-number flag; a front-only component
// also needs the number input.
export function unionPrintFlags(components) {
  const u = { name: false, initials: false, number: false };
  components.forEach((c) => {
    const f = (c && c.flags) || {};
    if (f.name) u.name = true;
    if (f.initials) u.initials = true;
    if (f.number || f.front) u.number = true;
  });
  return u;
}

// The _print_* properties a single component should carry: only the fields its
// own flags allow AND that have a (trimmed) value.
export function scopedPrintProps(flags, shared) {
  const out = {};
  const f = flags || {};
  const s = shared || {};
  // Name and initials are printed in capitals (the inputs already show them
  // that way), so the summary and the order carry the same uppercase value.
  const put = (allow, key, val, upper) => {
    if (allow && val != null && String(val).trim() !== '') out[key] = upper ? String(val).trim().toUpperCase() : String(val).trim();
  };
  put(f.name, '_print_name', s.name, true);
  put(f.initials, '_print_initials', s.initials, true);
  // One shared number, written per component to the keys its flags allow
  // (`number` = print_back_numbers, `front` = print_front_numbers).
  put(f.front, '_print_front_number', s.number, false);
  put(f.number, '_print_back_number', s.number, false);
  return out;
}

// The Trykk (print) product variant map, injected globally by theme.liquid from
// theme settings: { name, initials, number (back), front_number } → variant id
// or null. (Duplicated from klubb-product.js — modules don't cross-import.)
export function readTrykkMap(doc) {
  const d = doc || (typeof document !== 'undefined' ? document : null);
  const el = d && d.getElementById('klubb-trykk-data');
  const empty = { name: null, initials: null, number: null, front_number: null };
  if (!el) return empty;
  try {
    const m = JSON.parse(el.textContent);
    return { name: m.name || null, initials: m.initials || null, number: m.number || null, front_number: m.front_number || null };
  } catch (e) {
    return empty;
  }
}

// One Trykk charge line per (component, used print type): name, initials,
// back number and front number, each only when the component's flag allows
// it, the shared value is filled and a Trykk product is configured. The lines
// carry no values — those stay on the component line — only `_print_for`
// (the component variant) and `base` (_bundle_id/_bundle_name/_klubb_club) so
// the cart groups and removes them with the package.
export function buildPrintItems(components, shared, trykk, base) {
  const items = [];
  const t = trykk || {};
  (components || []).forEach((c) => {
    const scoped = scopedPrintProps(c.flags, shared);
    const line = (variantId) => {
      const properties = Object.assign({}, base || {});
      properties._print_for = String(c.variantId);
      items.push({ id: variantId, quantity: 1, properties });
    };
    if (scoped._print_name && t.name) line(t.name);
    if (scoped._print_initials && t.initials) line(t.initials);
    if (scoped._print_back_number && t.number) line(t.number);
    if (scoped._print_front_number && t.front_number) line(t.front_number);
  });
  return items;
}

// Chips for the «Slik trykkes det» summary: one per print type, the number
// shown once even when it goes both front and back.
export function chipList(props) {
  const out = [];
  if (props._print_name) out.push(['NAVN', props._print_name]);
  if (props._print_initials) out.push(['INIT', props._print_initials]);
  const num = props._print_back_number || props._print_front_number;
  if (num) out.push(['NR', num]);
  return out;
}

// One /cart/add.js item per component, tagged with the shared bundle id, the
// active club, and that component's scoped print props.
// The package page URL (/spillerpakke/…) isn't a club-catalog path, so the
// server can't resolve the active club — fall back to the client cookie
// (window.klubb.activeClub) so package lines carry _klubb_club like plain PDPs.
export function resolveClubSlug(serverSlug, activeClub) {
  return serverSlug || (activeClub && activeClub.slug) || '';
}

// D38: in a fixed-price package the bundle is sold for one set price, so the
// per-line club discount must NOT also apply. Tag each line `_fixed_package`
// (a marker only: no discount Function reads it yet, ADR 0001). Sum-priced packages omit the
// tag, so each eligible component still gets its klubbpris.
export function buildAddItems(components, shared, bundleId, clubSlug, fixed) {
  return components.map((c) => {
    const properties = { _bundle_id: bundleId };
    if (clubSlug) properties._klubb_club = clubSlug;
    // D65 — a club-only component must leave the cart when the club changes, like a plain product
    // (klubb-cart-reconcile.js reads this token). A package used to carry none, so it survived a club
    // switch and was restamped for the new club. Blank = a general component, eligible for everyone.
    if (c.eligible) properties._eligible_clubs = c.eligible;
    if (fixed) properties._fixed_package = bundleId;
    Object.assign(properties, scopedPrintProps(c.flags, shared));
    return { id: c.variantId, quantity: 1, properties };
  });
}

/* ============================================================
 * Browser wiring (auto-init). Validation mirrors klubb-product.js (kept local
 * to avoid a cross-module import between fingerprinted theme assets).
 * ============================================================ */

const PRINT_PATTERNS = { name: /^[A-Za-zÆØÅæøå ]+$/, initials: /^[A-Za-zÆØÅæøå]+$/, number: /^[0-9]+$/ };
const PRINT_MAX = { name: 12, initials: 3, number: 2 }; // TODO: confirm limits with ScanTrade

function validatePrintField(value, type) {
  const v = value == null ? '' : String(value);
  if (v === '') return true;
  if (PRINT_MAX[type] != null && v.length > PRINT_MAX[type]) return false;
  const p = PRINT_PATTERNS[type];
  return p ? p.test(v) : true;
}

function escapeHtml(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (ch) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

function init() {
  const root = document.querySelector('[data-klubb-pp]');
  if (!root) return;
  const dataEl = root.querySelector('[data-klubb-pp-components]');
  let components;
  try { components = JSON.parse(dataEl.textContent); } catch (e) { return; }
  if (!Array.isArray(components) || components.length === 0) return;

  const clubSlugAttr = root.getAttribute('data-klubb-pp-club') || '';
  const bundlePrefix = root.getAttribute('data-klubb-pp-bundle-prefix') || 'pkg';
  const untrykkLabel = root.getAttribute('data-klubb-pp-untrykk') || 'Uten trykk';
  // D38: 'fixed' packages are all-or-nothing (one sold-out component blocks the
  // whole bundle); 'sum' packages allow buying the available components.
  const priceMode = root.getAttribute('data-klubb-pp-price-mode') || 'sum';

  // D37: partner-mode clubs are display-only — mark the package so CSS hides
  // the add control (the club page explains how to order).
  const partnerClub = (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null;
  if (partnerClub && partnerClub.mode === 'partner') root.classList.add('is-partner');

  const chosen = components.map((c) => c.defaultVariantId);
  root.querySelectorAll('[data-klubb-pp-variant]').forEach((sel) => {
    const idx = Number(sel.getAttribute('data-klubb-pp-variant'));
    if (sel.value) chosen[idx] = Number(sel.value);
    sel.addEventListener('change', () => { chosen[idx] = Number(sel.value); });
  });

  const inputs = root.querySelectorAll('[data-klubb-pp-print]');
  const errorEl = root.querySelector('[data-klubb-pp-error]');
  const trk = root.querySelector('[data-klubb-pp-trk]');
  const confirmWrap = root.querySelector('[data-klubb-pp-confirm-wrap]');
  const confirmCb = root.querySelector('[data-klubb-pp-confirm]');
  const addBtn = root.querySelector('[data-klubb-pp-add]');
  const addLabel = root.querySelector('[data-klubb-pp-add-label]');
  const oosNote = root.querySelector('[data-klubb-pp-oos-note]');

  // Component-level OOS (the select already disables individual OOS variants).
  const oosIdx = components.reduce((acc, c, i) => (c.available === false ? acc.concat(i) : acc), []);

  function readShared() {
    const get = (type) => {
      const el = root.querySelector(`[data-klubb-pp-print][data-klubb-print-type="${type}"]`);
      return el ? el.value : '';
    };
    return { name: get('name'), initials: get('initials'), number: get('number') };
  }

  function refresh() {
    const shared = readShared();
    let hasValue = false;
    let allValid = true;
    inputs.forEach((el) => {
      if ((el.value || '').trim() !== '') hasValue = true;
      const ok = validatePrintField(el.value, el.getAttribute('data-klubb-print-type'));
      el.classList.toggle('is-invalid', !ok);
      if (!ok) allValid = false;
    });
    if (errorEl) errorEl.hidden = allValid;

    if (trk) {
      trk.hidden = !(hasValue && allValid);
      components.forEach((c, i) => {
        const valEl = root.querySelector(`[data-klubb-pp-trk-row="${i}"] [data-klubb-pp-trk-val]`);
        if (!valEl) return;
        const chips = chipList(scopedPrintProps(c.flags, shared));
        if (chips.length === 0) {
          valEl.className = 'klubb-pp__trk-val klubb-pp__trk-val--none';
          valEl.textContent = untrykkLabel;
        } else {
          valEl.className = 'klubb-pp__trk-val';
          valEl.innerHTML = chips
            .map((ch) => `<span class="klubb-pp__pchip"><b>${ch[0]}</b>${escapeHtml(ch[1])}</span>`)
            .join('');
        }
      });
    }

    if (confirmWrap) confirmWrap.hidden = !(hasValue && allValid);
    if (confirmCb && !(hasValue && allValid)) confirmCb.checked = false;

    if (oosNote) oosNote.hidden = oosIdx.length === 0;
    if (addLabel && addBtn) {
      addLabel.textContent = oosIdx.length > 0
        ? (addBtn.getAttribute('data-klubb-pp-label-without') || addLabel.textContent)
        : (addBtn.getAttribute('data-klubb-pp-label-add') || addLabel.textContent);
    }

    const printOk = !hasValue || (allValid && confirmCb && confirmCb.checked);
    // Sum: buyable while at least one component is in stock. Fixed: blocked the
    // moment any component is out of stock.
    const canBuy = priceMode === 'fixed' ? oosIdx.length === 0 : oosIdx.length < components.length;
    if (addBtn) addBtn.disabled = !(printOk && canBuy);
  }

  function addToCart() {
    // Fixed packages are all-or-nothing — a single OOS component blocks the add.
    if (priceMode === 'fixed' && oosIdx.length > 0) return;
    const shared = readShared();
    const selected = components
      .map((c, i) => ({ variantId: chosen[i], flags: c.flags, eligible: c.eligible, _i: i }))
      .filter((c) => oosIdx.indexOf(c._i) === -1);
    if (selected.length === 0) return;
    const bundleId = bundlePrefix + '-b' + Date.now().toString(36);
    const clubSlug = resolveClubSlug(clubSlugAttr, (typeof window !== 'undefined' && window.klubb && window.klubb.activeClub) || null);
    const items = buildAddItems(selected, shared, bundleId, clubSlug, priceMode === 'fixed');
    const pkgName = root.getAttribute('data-klubb-pp-name') || '';
    if (pkgName) items.forEach((it) => { it.properties._bundle_name = pkgName; });
    // Trykk charge lines for the components that get a print, grouped with the
    // package by _bundle_id so the cart shows and removes them together.
    const printBase = { _bundle_id: bundleId };
    if (clubSlug) printBase._klubb_club = clubSlug;
    if (pkgName) printBase._bundle_name = pkgName;
    const printItems = buildPrintItems(selected, shared, readTrykkMap(document), printBase);
    if (addBtn) addBtn.disabled = true;
    const payload = cartAddOrder(items, printItems);
    // D62 — window.klubbCart.add (assets/klubb-cart-api.js) adds and, in the same step, writes the
    // order attributes (klubb_club, traede_customer_id) and the club's klubbpris code. Without that
    // script the plain add does the add and the cart page finishes the job, as before D62.
    const api = typeof window !== 'undefined' ? window.klubbCart : null;
    const added = api
      ? api.add(payload)
      : fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: payload }),
      }).then((r) => { if (!r.ok) throw new Error('add failed'); return r.json(); });
    added
      .then((result) => {
        // D66: 'cart' = the page goes to /cart; 'drawer' = the cart drawer opened over this page, so free the button.
        const ending = api && typeof api.afterAdd === 'function' ? api.afterAdd(result) : (window.location.href = '/cart', 'cart');
        if (ending === 'drawer' && addBtn) addBtn.disabled = false;
      })
      .catch(() => { if (addBtn) addBtn.disabled = false; });
  }

  inputs.forEach((el) => el.addEventListener('input', refresh));
  if (confirmCb) confirmCb.addEventListener('change', refresh);
  if (addBtn) addBtn.addEventListener('click', addToCart);
  refresh();
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}

/**
 * /cart/add.js prepends each item in turn, so the last item sent ends up on
 * top. Reverse components plus Trykk lines: the cart and the checkout then
 * list the package in its own order with the print charges last.
 */
export function cartAddOrder(mainItems, printItems) {
  return mainItems.concat(printItems).reverse();
}
